import React, { useMemo, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Mail, Plus, X, Zap, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import StatusPackHistory from "./StatusPackHistory";

const DAYS = [
  { v: 0, l: "Monday",   s: "Mon" },
  { v: 1, l: "Tuesday",  s: "Tue" },
  { v: 2, l: "Wednesday",s: "Wed" },
  { v: 3, l: "Thursday", s: "Thu" },
  { v: 4, l: "Friday",   s: "Fri" },
  { v: 5, l: "Saturday", s: "Sat" },
  { v: 6, l: "Sunday",   s: "Sun" },
];

// Fast local-hour label helper — "9:00 AM", "12:00 PM", "11:00 PM"
const hourLabel = (h) => `${h % 12 || 12}:00 ${h < 12 ? "AM" : "PM"}`;

export default function WeeklyReportsPanel({ projectId, project, onUpdate, stakeholders = [] }) {
  const enabled = !!project.weekly_report_enabled;
  const emailStakeholders = !!project.email_stakeholders;
  const picked = stakeholders.filter((s) => (project.stakeholder_ids || []).includes(s.id));
  const pickedWithEmail = picked.filter((s) => s.email);
  const recipients = project.report_recipients || [];
  const dayOfWeek = Number(project.report_day_of_week ?? 0);
  const hourUtc = Number(project.report_hour_utc ?? 9);
  const [newEmail, setNewEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Browser offset from UTC in whole hours, honoring DST at render time.
  // getTimezoneOffset() returns MINUTES BEHIND UTC, so east = negative.
  const offsetHrs = useMemo(() => -Math.round(new Date().getTimezoneOffset() / 60), []);
  const tzLabel = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "local"; }
    catch { return "local"; }
  }, []);

  // UTC → local hour for display; local → UTC on save.
  const localHour = ((hourUtc + offsetHrs) % 24 + 24) % 24;
  const localHourOptions = useMemo(
    () => Array.from({ length: 24 }, (_, lh) => ({
      localHour: lh,
      utcHour: ((lh - offsetHrs) % 24 + 24) % 24,
      label: hourLabel(lh),
    })),
    [offsetHrs]
  );

  const toggle = async () => {
    try {
      const next = !enabled;
      await api.put(`/projects/${projectId}`, { data: { weekly_report_enabled: next } });
      onUpdate({ weekly_report_enabled: next });
      toast.success(next ? "Auto-send turned on" : "Auto-send turned off — you can still send on demand");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleStakeholders = async () => {
    const next = !emailStakeholders;
    try {
      await api.put(`/projects/${projectId}`, { data: { email_stakeholders: next } });
      onUpdate({ email_stakeholders: next });
      toast.success(next ? "Selected stakeholders will receive status reports" : "Stakeholders removed from status reports");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setDay = async (v) => {
    try {
      await api.put(`/projects/${projectId}`, { data: { report_day_of_week: v } });
      onUpdate({ report_day_of_week: v });
      toast.success(`Auto-send day → ${DAYS[v].l}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setHour = async (utcH) => {
    try {
      await api.put(`/projects/${projectId}`, { data: { report_hour_utc: utcH } });
      onUpdate({ report_hour_utc: utcH });
      const lh = ((utcH + offsetHrs) % 24 + 24) % 24;
      toast.success(`Auto-send time → ${hourLabel(lh)} (${tzLabel})`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addRecipient = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    const next = [...recipients.filter((r) => r !== email), email];
    try {
      await api.put(`/projects/${projectId}`, { data: { report_recipients: next } });
      onUpdate({ report_recipients: next });
      setNewEmail("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeRecipient = async (email) => {
    const next = recipients.filter((r) => r !== email);
    try {
      await api.put(`/projects/${projectId}`, { data: { report_recipients: next } });
      onUpdate({ report_recipients: next });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.post(`/projects/${projectId}/send-weekly-now`);
      toast.success("Status report queued — check your inbox");
      setTimeout(() => setRefreshKey((k) => k + 1), 1500);
    } catch (e) { toast.error(errMsg(e)); }
    setSending(false);
  };

  return (
    <div className="pc-card p-5 mb-6" data-testid="weekly-reports-panel">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300/80 mb-1">
            <Mail size={12}/> Status reports
          </div>
          <div className="text-lg font-semibold text-white" style={{fontFamily:'Outfit'}}>
            On-demand + scheduled
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Send a branded status pack any time, or auto-send one on a day and hour you pick — same format either way.
          </p>
        </div>

        <button
          onClick={sendNow}
          disabled={sending}
          data-testid="send-now-btn"
          className="inline-flex items-center gap-1 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg shadow-blue-900/30 disabled:opacity-60"
        >
          <Zap size={14}/> {sending ? "Sending…" : "Send report now"}
        </button>
      </div>

      <div className="mt-4 border-t border-slate-800 pt-4 flex items-start gap-4 flex-wrap">
        <label className="inline-flex items-center cursor-pointer" data-testid="weekly-toggle">
          <input type="checkbox" className="sr-only" checked={enabled} onChange={toggle}/>
          <span className={`relative inline-block w-11 h-6 rounded-full transition-colors ${enabled ? "bg-blue-600" : "bg-slate-700"}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${enabled ? "translate-x-5" : ""}`}></span>
          </span>
          <span className="ml-3 text-sm text-slate-200">
            Auto-send weekly
            <span className="block text-[11px] text-slate-500">
              {enabled
                ? `Every ${DAYS[dayOfWeek].l} at ${hourLabel(localHour)} ${tzLabel} · ${String(hourUtc).padStart(2,"0")}:00 UTC`
                : "Off"}
            </span>
          </span>
        </label>

        {enabled && (
          <>
            <div className="flex items-center gap-2" data-testid="day-picker">
              <Calendar size={14} className="text-slate-400"/>
              <div className="inline-flex rounded-lg border border-slate-700 overflow-hidden">
                {DAYS.map((d) => (
                  <button
                    key={d.v}
                    onClick={() => setDay(d.v)}
                    data-testid={`day-${d.v}`}
                    className={`px-2.5 py-1.5 text-xs ${dayOfWeek === d.v ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}
                    title={d.l}
                  >
                    {d.s}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2" data-testid="hour-picker">
              <Clock size={14} className="text-slate-400"/>
              <select
                value={localHour}
                onChange={(e) => {
                  const lh = Number(e.target.value);
                  const utc = ((lh - offsetHrs) % 24 + 24) % 24;
                  setHour(utc);
                }}
                data-testid="hour-select"
                className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500"
                title={`Local time (${tzLabel})`}
              >
                {localHourOptions.map((o) => (
                  <option key={o.localHour} value={o.localHour}>{o.label}</option>
                ))}
              </select>
              <span className="text-[11px] text-slate-500">{tzLabel}</span>
            </div>
          </>
        )}
      </div>

      {enabled && (
        <div className="mt-4 border-t border-slate-800 pt-4">
          <label className="inline-flex items-start cursor-pointer mb-4" data-testid="email-stakeholders-toggle">
            <input type="checkbox" className="sr-only" checked={emailStakeholders} onChange={toggleStakeholders}/>
            <span className={`relative inline-block w-11 h-6 shrink-0 rounded-full transition-colors ${emailStakeholders ? "bg-blue-600" : "bg-slate-700"}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${emailStakeholders ? "translate-x-5" : ""}`}></span>
            </span>
            <span className="ml-3 text-sm text-slate-200">
              Also email selected stakeholders
              <span className="block text-[11px] text-slate-500" data-testid="email-stakeholders-hint">
                {picked.length === 0
                  ? "No stakeholders selected on this project yet"
                  : `${pickedWithEmail.length} of ${picked.length} selected stakeholder${picked.length === 1 ? "" : "s"} ha${pickedWithEmail.length === 1 ? "s" : "ve"} an email${pickedWithEmail.length ? `: ${pickedWithEmail.map((s) => s.name).join(", ")}` : ""}`}
              </span>
            </span>
          </label>
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Additional recipients</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {recipients.length === 0 && <span className="text-slate-500 text-sm">Just the project owner for now.</span>}
            {recipients.map((r) => (
              <span key={r} className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 text-blue-200 text-sm px-2.5 py-1 rounded-full" data-testid={`recipient-${r}`}>
                {r}
                <button onClick={() => removeRecipient(r)} className="text-blue-300 hover:text-rose-300"><X size={12}/></button>
              </span>
            ))}
          </div>
          <form onSubmit={addRecipient} className="flex gap-2 items-center">
            <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="stakeholder@example.com" data-testid="recipient-email"
              className="flex-1 max-w-xs px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"/>
            <button type="submit" data-testid="add-recipient-btn" className="inline-flex items-center gap-1 border border-slate-700 hover:border-blue-500 text-slate-200 px-3 py-2 rounded-lg text-sm"><Plus size={14}/> Add</button>
          </form>

          <div className="mt-4 pt-4 border-t border-slate-800">
            <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1">Save-to folder (local or mapped drive)</label>
            <input
              type="text"
              defaultValue={project.report_save_path || ""}
              onBlur={async (e) => {
                const v = e.target.value.trim();
                if (v === (project.report_save_path || "")) return;
                try {
                  await api.put(`/projects/${projectId}`, { data: { report_save_path: v } });
                  onUpdate({ report_save_path: v });
                  toast.success(v ? "Save-to folder updated" : "Save-to folder cleared");
                } catch (err) { toast.error(errMsg(err)); }
              }}
              placeholder={`e.g.  C:\\Reports\\${project.name || "Project"}\\   or   ~/Compass/${project.name || "Project"}/`}
              data-testid="report-save-path"
              className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm font-mono"
            />
            <p className="text-[11px] text-slate-500 mt-1 leading-snug">
              Compass PM can't write to your disk on a schedule (browsers don't allow it) — this is a hint shown on every archived report and used as the suggested filename location when you click Download. For true unattended drops, set this path as your browser's default Downloads folder.
            </p>
          </div>
        </div>
      )}

      <StatusPackHistory projectId={projectId} refreshKey={refreshKey} />
    </div>
  );
}

